import React, { useState, useEffect } from 'react';
import '../css/SQLEditor.css';
import { useAuth } from '../AuthContext';
import CodeEditor from "@uiw/react-textarea-code-editor";

const SQLEditor = () => {
  const { isAdmin } = useAuth();

  
  const [requetes, setRequetes] = useState([{ 
    id: 1, 
    texte: 'SELECT * FROM finess;', 
    estActive: true 
  }]);
  const [idRequeteActive, setIdRequeteActive] = useState(1);
  const [resultatsRequete, setResultatsRequete] = useState(null);
  const [ongletActif, setOngletActif] = useState('resultats');
  const [enChargement, setEnChargement] = useState(false);
  const [chargementSchema, setChargementSchema] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [schemaTables, setSchemaTables] = useState([]);

  // Charger le schéma de la base de données au montage du composant
  useEffect(() => {
    const chargerSchemaBDD = async () => {
      if (!isAdmin) return;
      
      setChargementSchema(true);
      try {
        const reponse = await fetch('https://carto.api.toobib.org/api/schema');
        if (!reponse.ok) throw new Error('Échec du chargement du schéma');
        const donnees = await reponse.json();
        setSchemaTables(donnees);
      } catch (err) {
        console.error('Erreur chargement schéma:', err);
        setErreur(`Échec chargement schéma: ${err.message}`);
      } finally {
        setChargementSchema(false);
      }
    };

    chargerSchemaBDD();
  }, [isAdmin]);

  // Gestionnaire de changement de requête
  const gererChangementRequete = (id, nouveauTexte) => {
    setRequetes(requetes.map(requete => 
      requete.id === id ? { ...requete, texte: nouveauTexte } : requete
    ));
  };

  // Ajouter une nouvelle requête
  const ajouterNouvelleRequete = () => {
    const nouvelId = requetes.length > 0 ? Math.max(...requetes.map(r => r.id)) + 1 : 1;
    setRequetes([...requetes, { id: nouvelId, texte: '', estActive: false }]);
    
    
  };

  // Définir la requête active
  const definirRequeteActive = (id) => {
    setIdRequeteActive(id);
    setRequetes(requetes.map(requete => 
      ({ ...requete, estActive: requete.id === id })
    ));
  };

  // Exécuter la requête SQL
  const executerRequete = async () => {
    const requeteActive = requetes.find(r => r.id === idRequeteActive);
    if (!requeteActive?.texte.trim()) return;
  
    setEnChargement(true);
    setErreur(null);
    setOngletActif('resultats');
    try {
      const reponse = await fetch('https://carto.api.toobib.org/api/execute-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: requeteActive.texte.trim() 
        })
      });
  
      const donnees = await reponse.json();
  
      if (!reponse.ok) throw new Error(donnees.message || 'Erreur SQL');
  
      setResultatsRequete(donnees);
  
    } catch (err) {
      setErreur(err.message);
    } finally {
      setEnChargement(false);
    }
  };

  // Gestionnaire de raccourci clavier Ctrl+Entrée
  const gererToucheClavier = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      executerRequete();
    }
    else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); 
    }
  };

  // Composant pour afficher les métadonnées en bas des résultats
  const PiedDePageMetadonnees = ({ resultats }) => {
    if (!resultats) return null;
  
    return (
      <div className="pied-de-page-metadonnees">
        <div className="item-metadonnee">
          <span className="icone-metadonnee">⏱</span>
          <span className="valeur-metadonnee">{resultats.executionTime || 'N/A'}</span>
        </div>
        
        <div className="item-metadonnee">
          <span className="icone-metadonnee">📊</span>
          <span className="valeur-metadonnee">{resultats.rowCount} ligne(s)</span>
        </div>
  
        {resultats.limited && (
          <div className="item-metadonnee avertissement">
            <span className="icone-metadonnee">⚠</span>
            <span>Limite automatique (100 lignes)</span>
          </div>
        )}
  
        <div className="item-metadonnee">
          <span className="icone-metadonnee">🕒</span>
          <span className="valeur-metadonnee">
            {new Date(resultats.timestamp).toLocaleTimeString()}
          </span>
        </div>
      </div>
    );
  };
  
  // Afficher les résultats sous forme de tableau
  const afficherResultats = () => {
    if (!resultatsRequete) return <div className="aucun-resultat">Aucun résultat à afficher</div>;
    
    return (
      <div className="conteneur-resultats">
        <div className="zone-defilement-resultats">
          <table className="grille-resultats">
            <thead>
              <tr>
                {resultatsRequete.columns.map(colonne => (
                  <th key={colonne}>
                    <div className="conteneur-entete">
                      {colonne}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resultatsRequete.rows.map((ligne, indexLigne) => (
                <tr key={indexLigne}>
                  {resultatsRequete.columns.map(colonne => (
                    <td key={`${indexLigne}-${colonne}`}>
                      <div className="contenu-cellule" title={String(ligne[colonne])}>
                        {String(ligne[colonne])}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PiedDePageMetadonnees resultats={resultatsRequete} />
      </div>
    );
  };

  // Pour fficher le schéma UML de la base de données
  const afficherSchemaUML = () => {
    if (chargementSchema) return <div className="chargement-schema">Chargement du schéma...</div>;
    if (erreur) return <div className="erreur-schema">Erreur: {erreur}</div>;
    if (!isAdmin) return <div className="acces-refuse">Accès réservé aux administrateurs</div>;
    if (schemaTables.length === 0) return <div className="schema-indisponible">Aucun schéma disponible</div>;

    return (
      <div className="conteneur-schema-uml">
        <div className="grille-tables-uml">
          {schemaTables.map(table => (
            <div key={table.name} className="carte-table-uml" id={`table-${table.name}`}>
              <div className="entete-table-uml">
                <h3>{table.name}</h3>
              </div>
              <div className="colonnes-table-uml">
                {table.columns.map(colonne => (
                  <div key={`${table.name}-${colonne.name}`} className="item-colonne-uml">
                    <div className="nom-type-colonne">
                      <span className="nom-colonne">{colonne.name}</span>
                      <span className="type-colonne">{colonne.type}</span>
                    </div>
                    <div className="contraintes-colonne">
                      {colonne.primaryKey && <span className="contrainte pk">PK</span>}
                      {colonne.foreignKey && <span className="contrainte fk">FK</span>}
                      {colonne.isNullable && <span className="contrainte nullable">NULL</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rendu principal du composant
  return (
    <div>
      {!isAdmin ? (
        <div className="conteneur-validation">
          <div className="non-autorise">
            <h2>Accès Refusé</h2>
            <p>
              Vous ne pouvez accéder à cette page sans entrer votre jeton de connexion sur l'
              <a href="https://carto.toobib.org" className="lien-application">application</a>
            </p>
          </div>
        </div>
      ) : (
        <div className="page-editeur-sql">
          <div className="section-superieure-editeur">
            <div className="onglets-resultats">
              <button 
                className={`bouton-onglet ${ongletActif === 'resultats' ? 'actif' : ''}`}
                onClick={() => setOngletActif('resultats')}
              >
                Résultats
              </button>
              <button 
                className={`bouton-onglet ${ongletActif === 'diagramme' ? 'actif' : ''}`}
                onClick={() => setOngletActif('diagramme')}
              >
                Schéma UML
              </button>
            </div>
            
            <div className="affichage-resultats">
              {ongletActif === 'resultats' ? afficherResultats() : afficherSchemaUML()}
            </div>
          </div>
          
          <div className="section-inferieure-editeur">
            <div className="ligne-onglets-requetes">
              {requetes.map(requete => (
                <div 
                  key={requete.id}
                  className={`item-onglet-requete ${requete.estActive ? 'actif' : ''}`}
                  onClick={() => definirRequeteActive(requete.id)}
                >
                  Requête {requete.id}
                </div>
              ))}
              <button className="bouton-ajouter-requete" onClick={ajouterNouvelleRequete}>
                <i className="icone-plus"></i>
              </button>
            </div>
            
            <div className="zone-saisie-requete">
            <CodeEditor
            value={requetes.find(r => r.id === idRequeteActive)?.texte || ""}
            language="sql"
            onKeyDown={gererToucheClavier}
            placeholder="Écrivez votre requête SQL ici..."
            onChange={(e) => gererChangementRequete(idRequeteActive, e.target.value)}
            className="zone-texte-sql"
            style={{fontSize: '17px'}}
            
          />
            </div>
            
            <div className="barre-actions-requete">
              <button 
                className="bouton-executer bouton-primaire" 
                onClick={executerRequete}
                disabled={enChargement}
                title="Ctrl+Entrée"
              >
                {enChargement ? (
                  <>
                    <i className="icone-chargement rotation"></i>
                    Exécution...
                  </>
                ) : (
                  <>
                    Exécuter
                  </>
                )}
              </button>
              {erreur && (
                <div className="message-erreur">
                  <i className="icone-erreur"></i>
                  {erreur}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SQLEditor;